import axios from "axios";
import { jwtDecode } from "jwt-decode";
let apiBaseUrl = "";
let axiosInstance = null;
const API_PREFIX = "/api/v1";
// Mirrors identity-core's/workspaces-core's `workspaceStorageKey()`/`siteScopedStorageKey()`
// exactly -- selected-workspace memory is scoped per site even though several sites share one
// runtime origin (e.g. ui.dev.offsetmind.com/hearthbook and /karqo). Each package computes the
// same key independently since they're separate npm packages; keep them in sync if this changes.
const workspaceStorageKey = () => {
    const siteHostname = typeof window !== "undefined" ? window.__COREMIND_SITE_HOSTNAME__ : null;
    return siteHostname ? `coremind:workspace-id:${siteHostname}` : "coremind:workspace-id";
};
// 🍪 Retrieve cookie by name
const getCookie = (name) => {
    const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
    return match ? decodeURIComponent(match[2]) : null;
};
// ⏳ Check token expiration
const isTokenExpired = (token) => {
    try {
        const decoded = jwtDecode(token);
        console.log("⏰ Token expires at:", new Date(decoded.exp * 1000).toISOString());
        console.log("🕒 Current time:", new Date().toISOString());
        return decoded.exp * 1000 < Date.now();
    }
    catch (error) {
        console.error("❌ Error decoding token:", error);
        return true;
    }
};
const resolveUserId = (token) => {
    if (!token)
        return null;
    try {
        const decoded = jwtDecode(token);
        return decoded.sub || decoded["cognito:username"] || null;
    }
    catch (error) {
        console.error("❌ Error decoding token for user ID:", error);
        return null;
    }
};
const normalizeApiBaseUrl = (url) => {
    const trimmed = (url || "").trim().replace(/\/+$/, "");
    if (!trimmed) {
        return trimmed;
    }
    if (trimmed.endsWith(API_PREFIX)) {
        return trimmed;
    }
    return `${trimmed}${API_PREFIX}`;
};
// 🌐 Set base URL and create instance
export const setApiBaseUrl = (url) => {
    apiBaseUrl = normalizeApiBaseUrl(url);
    axiosInstance = createAxiosInstance();
};
// 🛠 Create and configure axios instance
export const createAxiosInstance = () => {
    const instance = axios.create({
        baseURL: apiBaseUrl,
        headers: {
            "Content-Type": "application/json",
        },
        withCredentials: true, // ⚠️ Keep this if your backend expects cookies on the request
    });
    instance.interceptors.request.use((config) => {
        const base = config.baseURL ?? apiBaseUrl;
        if (typeof base === "string" &&
            /\/api\/v1\/?$/.test(base) &&
            typeof config.url === "string" &&
            config.url.startsWith("/api/v1/")) {
            config.url = config.url.replace(/^\/api\/v1/, "");
        }
        const token = getCookie("accessToken");
        console.log("🔐 Access token from cookies:", token);
        if (token && !isTokenExpired(token)) {
            config.headers["Authorization"] = `Bearer ${token}`;
            const userId = resolveUserId(token);
            if (userId) {
                config.headers["X-User-Id"] = userId;
            }
            console.log("✅ Token attached to request.");
        }
        else {
            console.warn("⚠️ No valid token found. Request may fail.");
        }
        if (typeof window !== "undefined") {
            // tenant_id and workspace_id are used interchangeably in this platform -- the workspace
            // switcher's selection (workspaces-core) IS the user's intended tenant context for every
            // request, including which of *their* workspaces' data shows up on a forked site like this
            // one (e.g. inventory items can live under a non-default workspace). Sending it is correct
            // and required; the coremind-api side now falls back to the site's own tenant only when
            // this header is absent at all, so an unset workspace-id is still handled safely.
            const workspaceId = window.localStorage.getItem(workspaceStorageKey());
            if (workspaceId) {
                config.headers["X-Tenant-Id"] = workspaceId;
            }
            // Sites that share an environment's main coremind-api can't be identified from the
            // request's Host/Origin header alone -- forward the host's own site identity (set by
            // foundation-shell-core's createSiteShell) so the API resolves the right PlatformSite.
            const siteHostname = window.__COREMIND_SITE_HOSTNAME__;
            if (typeof siteHostname === "string" && siteHostname.trim()) {
                config.headers["X-Coremind-Site-Host"] = siteHostname.trim();
            }
        }
        return config;
    });
    return instance;
};
// 🔁 Safe getter to retrieve initialized axios
export const getAxios = () => {
    if (!axiosInstance) {
        const windowApi = typeof window !== "undefined"
            ? window.__COREMIND_API_URL__
            : "";
        if (windowApi) {
            setApiBaseUrl(windowApi);
        }
    }
    if (!axiosInstance) {
        throw new Error("Axios instance not initialized. Call setApiBaseUrl() first.");
    }
    return axiosInstance;
};
